<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { useUserStore } from '@/store/user-store'

// Props
const props = defineProps({
  isRecommendationDialogOpen: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['update:isRecommendationDialogOpen'])

// State
const userStore = useUserStore()
const loading = ref(false)
const error = ref(null)
const recommendationData = ref(null)
const commentaryRef = ref(null)
const noRecommendationReason = ref(null) // Track why no recommendations are available

// Speech API state
const speechSynthesis = window.speechSynthesis
const currentUtterance = ref(null)
const isPlaying = ref(false)
const isPaused = ref(false)
const currentCharIndex = ref(0)
const totalLength = ref(0)
const speechText = ref('')
const currentSentenceIndex = ref(-1)
const highlightedText = ref('')

// Computed properties
const isOpen = computed({
  get: () => props.isRecommendationDialogOpen,
  set: (value) => emit('update:isRecommendationDialogOpen', value)
})

const formattedContent = computed(() => {
  return recommendationData.value ? formatCommentary() : null
})

const progressPercentage = computed(() => {
  if (totalLength.value === 0) return 0
  return (currentCharIndex.value / totalLength.value) * 100
})

// Computed property for highlighted text sections
const highlightedSections = computed(() => {
  if (!recommendationData.value || !isPlaying.value) return null

  const sections = ['summary', 'context', 'recommendations']
  const result = {}

  sections.forEach(section => {
    if (recommendationData.value[section]) {
      result[section] = recommendationData.value[section].map((item, index) => {
        // Use clean text for speech matching
        const cleanText = item.replace(/\[(positive|negative|neutral)\]$/i, '').trim()
        const itemText = `${cleanText}. `
        const itemStart = speechText.value.indexOf(itemText, getTextOffset(section, index))
        const itemEnd = itemStart + itemText.length

        // Check if current reading position is within this item
        const isCurrentlyReading = currentCharIndex.value >= itemStart && currentCharIndex.value <= itemEnd

        // Auto-scroll to highlighted item
        if (isCurrentlyReading) {
          nextTick(() => {
            scrollToHighlightedItem(section, index)
          })
        }

        return {
          text: item,
          isHighlighted: isCurrentlyReading
        }
      })
    }
  })

  return result
})

// Computed property for processed sections with sentiment analysis
const processedSections = computed(() => {
  if (!recommendationData.value) return null

  return {
    summary: parseSentimentData(recommendationData.value.summary || []),
    context: parseSentimentData(recommendationData.value.context || []),
    recommendations: parseSentimentData(recommendationData.value.recommendations || [])
  }
})

// Helper function to parse sentiment and clean text
const parseSentimentData = (items) => {
  return items.map(item => {
    const sentimentMatch = item.match(/\[(positive|negative|neutral)\]$/i)
    const sentiment = sentimentMatch ? sentimentMatch[1].toLowerCase() : 'neutral'
    const cleanText = item.replace(/\[(positive|negative|neutral)\]$/i, '').trim()
    const hasNumbers = /\d+/.test(cleanText)

    return {
      originalText: item,
      cleanText,
      sentiment,
      hasNumbers,
      icon: getIconType(sentiment, hasNumbers)
    }
  })
}

// Helper function to determine icon type
const getIconType = (sentiment, hasNumbers) => {
  if (hasNumbers) {
    switch (sentiment) {
      case 'positive': return 'trend-up'
      case 'negative': return 'trend-down'
      case 'neutral': return 'trend-flat'
      default: return 'trend-flat'
    }
  } else {
    switch (sentiment) {
      case 'positive': return 'sentiment-positive'
      case 'negative': return 'sentiment-negative'
      case 'neutral': return 'sentiment-neutral'
      default: return 'sentiment-neutral'
    }
  }
}
const getTextOffset = (section, index) => {
  let offset = 0

  if (section === 'summary') {
    offset = speechText.value.indexOf('Summary:') + 8 // 8 for "Summary:"
  } else if (section === 'context') {
    offset = speechText.value.indexOf('Context:') + 8 // 8 for "Context:"
  } else if (section === 'recommendations') {
    offset = speechText.value.indexOf('Recommendations:') + 16 // 16 for "Recommendations:"
  }

  // Add offset for previous items in the same section
  if (recommendationData.value[section]) {
    for (let i = 0; i < index; i++) {
      // Clean text before calculating offset
      const cleanText = recommendationData.value[section][i].replace(/\[(positive|negative|neutral)\]$/i, '').trim()
      offset += `${cleanText}. `.length
    }
  }

  return offset
}

// Watch for dialog open
watch(isOpen, async (newValue) => {
  if (newValue) {
    await loadRecommendations()
  } else {
    stopAudio()
  }
})

// Methods
const closeModal = () => {
  stopAudio()
  isOpen.value = false
}

const loadRecommendations = async () => {
  loading.value = true
  error.value = null
  noRecommendationReason.value = null

  try {
    const response = await userStore.getRecommendationSummary()

    // Check if recommendationData exists and is not null
    if (response && response.responseData && response.responseData.recommendationData) {
      // Check if we have actual content in the recommendations
      const data = response.responseData.recommendationData
      const hasContent = (data.summary && data.summary.length > 0) ||
                        (data.context && data.context.length > 0) ||
                        (data.recommendations && data.recommendations.length > 0)

      if (hasContent) {
        recommendationData.value = data

        // Prepare speech text
        await nextTick()
        prepareSpeechText()

        // Auto-play when content loads
        setTimeout(() => {
          playAudio()
        }, 500)
      } else {
        // No content in recommendations - being generated
        noRecommendationReason.value = 'empty'
      }
    } else {
      // recommendationData is null or missing - being generated
      noRecommendationReason.value = 'empty'
    }
  } catch (err) {
    console.error('Failed to load recommendations:', err)

    // Check if it's a specific "no recommendations" error vs general API error
    if (err.message && err.message.includes('recommendation')) {
      noRecommendationReason.value = 'never_generated'
    } else {
      error.value = 'Failed to load portfolio recommendations. Please try again.'
    }
  } finally {
    loading.value = false
  }
}

const formatCommentary = () => {
  if (!recommendationData.value) return ''

  let content = 'Summary:\n'
  recommendationData.value.summary?.forEach(item => {
    // Remove sentiment tags before adding to speech text
    const cleanText = item.replace(/\[(positive|negative|neutral)\]$/i, '').trim()
    content += `${cleanText}. `  // Add period and space for natural pause
  })

  content += '\n\nContext:\n'
  recommendationData.value.context?.forEach(item => {
    // Remove sentiment tags before adding to speech text
    const cleanText = item.replace(/\[(positive|negative|neutral)\]$/i, '').trim()
    content += `${cleanText}. `  // Add period and space for natural pause
  })

  content += '\n\nRecommendations:\n'
  recommendationData.value.recommendations?.forEach(item => {
    // Remove sentiment tags before adding to speech text
    const cleanText = item.replace(/\[(positive|negative|neutral)\]$/i, '').trim()
    content += `${cleanText}. `  // Add period and space for natural pause
  })

  return content
}

const prepareSpeechText = () => {
  speechText.value = formattedContent.value || ''
  totalLength.value = speechText.value.length
  currentCharIndex.value = 0
}

const createUtterance = (text, startIndex = 0) => {
  const textToSpeak = text.substring(startIndex)
  const utterance = new SpeechSynthesisUtterance(textToSpeak)

  // Set voice settings
  utterance.lang = 'en-US'
  utterance.pitch = 1.0
  utterance.rate = 1.0
  utterance.volume = 1.0

  // Find English voice if available
  const voices = speechSynthesis.getVoices()
  const englishVoice = voices.find(voice =>
    voice.lang.startsWith('en-US') || voice.lang.startsWith('en')
  )
  if (englishVoice) {
    utterance.voice = englishVoice
  }

  // Track progress
  utterance.onboundary = (event) => {
    if (event.name === 'word' || event.name === 'sentence') {
      currentCharIndex.value = startIndex + event.charIndex
    }
  }

  utterance.onend = () => {
    isPlaying.value = false
    isPaused.value = false
    currentCharIndex.value = totalLength.value
  }

  utterance.onerror = (event) => {
    console.error('Speech synthesis error:', event.error)
    isPlaying.value = false
    isPaused.value = false
  }

  return utterance
}

const playAudio = () => {
  if (!speechText.value) return

  if (isPaused.value) {
    speechSynthesis.resume()
    isPlaying.value = true
    isPaused.value = false
  } else {
    stopAudio()
    currentUtterance.value = createUtterance(speechText.value, currentCharIndex.value)
    speechSynthesis.speak(currentUtterance.value)
    isPlaying.value = true
    isPaused.value = false
  }
}

const pauseAudio = () => {
  if (isPlaying.value) {
    speechSynthesis.pause()
    isPlaying.value = false
    isPaused.value = true
  }
}

const stopAudio = () => {
  speechSynthesis.cancel()
  isPlaying.value = false
  isPaused.value = false
  currentUtterance.value = null
}

const replayAudio = () => {
  stopAudio()
  currentCharIndex.value = 0
  playAudio()
}

const backwardAudio = () => {
  if (!speechText.value) return

  // Calculate approximate characters for 10 seconds
  const charsPerSecond = Math.ceil(totalLength.value / 60) // Assuming 1 minute total
  const backwardChars = charsPerSecond * 10

  currentCharIndex.value = Math.max(0, currentCharIndex.value - backwardChars)

  // If audio was playing, continue from new position
  if (isPlaying.value) {
    stopAudio()
    setTimeout(() => {
      playAudio()
    }, 100) // Small delay to ensure clean restart
  }
}

const forwardAudio = () => {
  if (!speechText.value) return

  // Calculate approximate characters for 10 seconds
  const charsPerSecond = Math.ceil(totalLength.value / 60)
  const forwardChars = charsPerSecond * 10

  currentCharIndex.value = Math.min(totalLength.value, currentCharIndex.value + forwardChars)

  // If audio was playing, continue from new position
  if (isPlaying.value) {
    stopAudio()
    setTimeout(() => {
      playAudio()
    }, 100) // Small delay to ensure clean restart
  }
}

// Auto-scroll to highlighted item
const scrollToHighlightedItem = (section, index) => {
  const element = document.querySelector(`[data-section="${section}"][data-index="${index}"]`)
  if (element) {
    // Find the scrollable container (modal-content)
    const container = document.querySelector('.modal-content')
    if (container) {
      const containerRect = container.getBoundingClientRect()
      const elementRect = element.getBoundingClientRect()

      // Calculate relative positions
      const elementTop = elementRect.top - containerRect.top + container.scrollTop
      const elementHeight = elementRect.height
      const containerHeight = containerRect.height

      // Check if element is visible within the container
      const isVisible = elementRect.top >= containerRect.top &&
                       elementRect.bottom <= containerRect.bottom

      // If not visible, scroll to center the element
      if (!isVisible) {
        const scrollTo = elementTop - (containerHeight / 2) + (elementHeight / 2)
        container.scrollTo({
          top: Math.max(0, scrollTo),
          behavior: 'smooth'
        })
      }
    }
  }
}// Helper function to get icon CSS classes
const getIconClass = (iconType) => {
  const baseClasses = ['pi']

  switch (iconType) {
    case 'trend-up':
      baseClasses.push('pi-arrow-up', 'trend-positive')
      break
    case 'trend-down':
      baseClasses.push('pi-arrow-down', 'trend-negative')
      break
    case 'trend-flat':
      baseClasses.push('pi-minus', 'trend-neutral')
      break
    case 'sentiment-positive':
      baseClasses.push('pi-thumbs-up', 'sentiment-positive')
      break
    case 'sentiment-negative':
      baseClasses.push('pi-thumbs-down', 'sentiment-negative')
      break
    case 'sentiment-neutral':
      baseClasses.push('pi-stop', 'sentiment-neutral')
      break
    default:
      baseClasses.push('pi-stop', 'sentiment-neutral')
  }

  return baseClasses.join(' ')
}

// Helper method to navigate to Portfolio Analytics
const navigateToAnalytics = () => {
  // Close the dialog first
  closeModal()

  // Navigate to Portfolio Analytics tab
  // This navigates to src/screens/dashboard/PortfolioAnalytics.vue
  if (userStore.setTabView) {
    userStore.setTabView('Portfolio Analytics')
  }
}// Helper method to contact support
const contactSupport = () => {
  // You can implement this to open a contact form, email, or phone
  const email = 'support@assetnivesh.com'
  const subject = 'Portfolio Recommendation Support Request'
  const body = 'Hi, I am unable to see my portfolio recommendations. Please help.'

  window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}
onUnmounted(() => {
  stopAudio()
})
</script>

<template>
  <div v-if="isOpen" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <h2 class="modal-title">Portfolio Recommendation Commentary</h2>
          <img
            src="@/assets/logo/Logo.png"
            alt="AssetNivesh Logo"
            class="header-logo"
          />
        </div>
        <button class="close-btn" @click="closeModal">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <!-- Modal Content -->
      <div class="modal-content">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <i class="pi pi-spinner pi-spin"></i>
          <p>Loading your portfolio recommendations...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
          <i class="pi pi-exclamation-triangle"></i>
          <p>{{ error }}</p>
          <button class="retry-btn" @click="loadRecommendations">Try Again</button>
        </div>

        <!-- No Recommendations State -->
        <div v-else-if="noRecommendationReason" class="no-recommendations-container">
          <!-- Scenario 1: Never generated recommendations -->
          <div v-if="noRecommendationReason === 'never_generated'" class="scenario-content">
            <div class="scenario-icon">
              <i class="pi pi-chart-line"></i>
            </div>
            <h3>Portfolio Recommendations Not Generated Yet</h3>
            <p class="scenario-description">
              You haven't generated portfolio recommendations yet. To get personalized investment
              insights and commentary, please visit the Portfolio Analytics page first.
            </p>
            <div class="scenario-actions">
              <button class="primary-btn" @click="navigateToAnalytics">
                <i class="pi pi-arrow-right"></i>
                Go to Portfolio Analytics
              </button>
              <button class="secondary-btn" @click="loadRecommendations">
                <i class="pi pi-refresh"></i>
                Check Again
              </button>
            </div>
          </div>

          <!-- Scenario 2: Recommendations being generated -->
          <div v-else-if="noRecommendationReason === 'empty'" class="scenario-content">
            <div class="scenario-icon processing">
              <i class="pi pi-spinner pi-spin"></i>
            </div>
            <h3>Recommendations Being Generated</h3>
            <p class="scenario-description">
              Your portfolio recommendations are currently being generated. This process may take
              5-10 minutes depending on your portfolio complexity. Please check back in a few minutes.
            </p>
            <div class="scenario-info">
              <div class="info-item">
                <i class="pi pi-clock"></i>
                <span>Expected time: 5-10 minutes</span>
              </div>
              <div class="info-item">
                <i class="pi pi-info-circle"></i>
                <span>Analysis based on your current holdings</span>
              </div>
            </div>
            <div class="scenario-actions">
              <button class="primary-btn" @click="loadRecommendations">
                <i class="pi pi-refresh"></i>
                Check Again
              </button>
              <button class="secondary-btn" @click="contactSupport">
                <i class="pi pi-envelope"></i>
                Contact Support
              </button>
            </div>
            <div class="support-note">
              <p>
                <strong>Still no recommendations after 10 minutes?</strong><br>
                Please contact the AssetNivesh team for assistance.
              </p>
            </div>
          </div>
        </div>

        <!-- Content State -->
        <div v-else-if="formattedContent" class="content-container">
          <!-- Audio Controls -->
          <div class="audio-controls">
            <button class="control-btn" @click="playAudio" :disabled="isPlaying">
              <i class="pi pi-play"></i>
              Play
            </button>
            <button class="control-btn" @click="pauseAudio" :disabled="!isPlaying">
              <i class="pi pi-pause"></i>
              Pause
            </button>
            <button class="control-btn" @click="replayAudio">
              <i class="pi pi-refresh"></i>
              Replay
            </button>
            <button class="control-btn" @click="backwardAudio">
              <i class="pi pi-step-backward"></i>
              -10s
            </button>
            <button class="control-btn" @click="forwardAudio">
              <i class="pi pi-step-forward"></i>
              +10s
            </button>
          </div>

          <!-- Progress Indicator -->
          <div v-if="isPlaying || isPaused" class="progress-container">
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: progressPercentage + '%' }"
              ></div>
            </div>
            <span class="progress-text">{{ Math.round(progressPercentage) }}%</span>
          </div>

          <!-- Commentary Text -->
          <div class="commentary-text" ref="commentaryRef">
            <div class="section">
              <h3>Summary:</h3>
              <div class="section-items">
                <div
                  v-for="(item, index) in processedSections?.summary"
                  :key="'summary-' + index"
                  class="item-row"
                  :class="{ 'highlighted': highlightedSections && highlightedSections.summary && highlightedSections.summary[index]?.isHighlighted }"
                  :data-section="'summary'"
                  :data-index="index"
                >
                  <div class="item-text">
                    • {{ item.cleanText }}
                  </div>
                  <div class="item-icon">
                    <i v-if="item.icon !== 'sentiment-neutral'" :class="getIconClass(item.icon)"></i>
                    <div v-else class="neutral-icon sentiment-neutral">
                      <svg class="neutral-arrow" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L22 12L12 22L2 12L12 2Z" fill="#FFC107" stroke="#000" stroke-width="1.5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="section">
              <h3>Context:</h3>
              <div class="section-items">
                <div
                  v-for="(item, index) in processedSections?.context"
                  :key="'context-' + index"
                  class="item-row"
                  :class="{ 'highlighted': highlightedSections && highlightedSections.context && highlightedSections.context[index]?.isHighlighted }"
                  :data-section="'context'"
                  :data-index="index"
                >
                  <div class="item-text">
                    • {{ item.cleanText }}
                  </div>
                  <div class="item-icon">
                    <i v-if="item.icon !== 'sentiment-neutral'" :class="getIconClass(item.icon)"></i>
                    <div v-else class="neutral-icon sentiment-neutral">
                      <svg class="neutral-arrow" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L22 12L12 22L2 12L12 2Z" fill="#FFC107" stroke="#000" stroke-width="1.5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="section">
              <h3>Recommendations:</h3>
              <div class="section-items">
                <div
                  v-for="(item, index) in processedSections?.recommendations"
                  :key="'rec-' + index"
                  class="item-row"
                  :class="{ 'highlighted': highlightedSections && highlightedSections.recommendations && highlightedSections.recommendations[index]?.isHighlighted }"
                  :data-section="'recommendations'"
                  :data-index="index"
                >
                  <div class="item-text">
                    • {{ item.cleanText }}
                  </div>
                  <div class="item-icon">
                    <i v-if="item.icon !== 'sentiment-neutral'" :class="getIconClass(item.icon)"></i>
                    <div v-else class="neutral-icon sentiment-neutral">
                      <svg class="neutral-arrow" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L22 12L12 22L2 12L12 2Z" fill="#FFC107" stroke="#000" stroke-width="1.5"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 10px;
  width: 95%;
  max-width: 1200px; /* Increased from 800px to 1200px */
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e5e5;
  background: #f8f9fa;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between; /* Space between title and logo */
  flex: 1;
}

.header-logo {
  height: 32px;
  width: auto;
  margin-right: 16px; /* Pull logo left to give space from close button */
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--an-text-primary, #333);
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #e5e5e5;
  color: #333;
}

.modal-content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.loading-container i {
  font-size: 32px;
  color: var(--an-primary, #2196f3);
  margin-bottom: 16px;
}

.error-container i {
  font-size: 32px;
  color: #f44336;
  margin-bottom: 16px;
}

.retry-btn {
  background: var(--an-primary, #2196f3);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 16px;
  font-weight: 500;
  transition: background 0.2s ease;
}

.retry-btn:hover {
  background: var(--an-primary-dark, #1976d2);
}

/* No Recommendations Scenarios Styles */
.no-recommendations-container {
  padding: 40px 20px;
  text-align: center;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scenario-content {
  max-width: 500px;
  margin: 0 auto;
}

.scenario-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2196f3, #21cbf3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 4px 16px rgba(33, 150, 243, 0.3);
}

.scenario-icon.processing {
  background: linear-gradient(135deg, #ff9800, #ffb74d);
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.3);
}

.scenario-icon i {
  font-size: 32px;
  color: white;
}

.scenario-content h3 {
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: var(--an-text-primary, #333);
}

.scenario-description {
  font-size: 16px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 24px;
}

.scenario-info {
  margin: 20px 0;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #ff9800;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #555;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item i {
  color: #ff9800;
  width: 16px;
}

.scenario-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.primary-btn,
.secondary-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  border: none;
}

.primary-btn {
  background: var(--an-primary, #2196f3);
  color: white;
}

.primary-btn:hover {
  background: var(--an-primary-dark, #1976d2);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.secondary-btn {
  background: #f8f9fa;
  color: #555;
  border: 1px solid #ddd;
}

.secondary-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
  transform: translateY(-1px);
}

.support-note {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 6px;
  padding: 16px;
  margin-top: 20px;
}

.support-note p {
  margin: 0;
  font-size: 14px;
  color: #856404;
  line-height: 1.5;
}

.audio-controls {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.control-btn {
  background: var(--an-primary, #2196f3);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.control-btn:hover:not(:disabled) {
  background: var(--an-primary-dark, #1976d2);
  transform: translateY(-1px);
}

.control-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: #e5e5e5;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--an-primary, #2196f3);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #666;
  min-width: 35px;
}

.commentary-text {
  line-height: 1.6;
  color: var(--an-text-primary, #333);
}

.section {
  margin-bottom: 24px;
}

.section h3 {
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--an-primary, #2196f3);
  border-bottom: 2px solid var(--an-primary, #2196f3);
  padding-bottom: 4px;
}

.section-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
  min-height: 32px;
}

.item-text {
  flex: 1;
  font-size: 15px;
  line-height: 1.5;
  padding-left: 12px; /* Indent for bullet point */
}

.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f8f9fa;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.item-icon i {
  font-size: 16px;
  font-weight: bold;
}

.neutral-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.neutral-arrow {
  width: 16px;
  height: 16px;
}

.neutral-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
}

.item-row.highlighted {
  background-color: #0d7c68; /* Dark green background */
  color: white; /* Bold white text */
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(13, 124, 104, 0.3);
  transform: scale(1.02); /* Subtle scale effect */
}

.section-content {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.text-column {
  flex: 1;
}

.icon-column {
  width: 20%;
  min-width: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0; /* Remove gap to align with text lines */
}

.icon-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px; /* Match the line height of text items */
  width: 32px;
  border-radius: 50%;
  background: #f8f9fa;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  margin-bottom: 8px; /* Match the margin-bottom of li items */
}

.icon-item i {
  font-size: 16px;
  font-weight: bold;
}

/* Icon styling based on sentiment */
.trend-positive {
  color: #28a745;
}

.trend-negative {
  color: #dc3545;
}

.trend-neutral {
  color: #6c757d;
}

.sentiment-positive {
  color: #28a745;
}

.sentiment-negative {
  color: #dc3545;
}

.sentiment-neutral {
  color: #6c757d;
}

/* Icon background styling based on sentiment classes */
.item-icon:has(.trend-positive),
.item-icon:has(.sentiment-positive) {
  background: #e8f5e8;
  border-color: #28a745;
}

.item-icon:has(.trend-negative),
.item-icon:has(.sentiment-negative) {
  background: #fde8e8;
  border-color: #dc3545;
}

.item-icon:has(.trend-neutral),
.item-icon:has(.sentiment-neutral) {
  background: #f1f3f4;
  border-color: #6c757d;
}

.section ul {
  margin: 0;
  padding-left: 20px;
}

.section li {
  margin-bottom: 8px;
  font-size: 15px;
  transition: all 0.3s ease;
  padding: 4px 8px;
  border-radius: 4px;
  line-height: 1.5; /* Consistent line height */
  min-height: 24px; /* Minimum height to match icon alignment */
  display: flex;
  align-items: center; /* Center text vertically within the li */
}

.section li.highlighted {
  background-color: #0d7c68; /* Dark green background */
  color: white; /* Bold white text */
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(13, 124, 104, 0.3);
  transform: scale(1.02); /* Subtle scale effect */
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 20px;
  }

  .modal-content {
    padding: 16px;
  }

  .modal-header {
    padding: 16px 20px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .modal-title {
    font-size: 18px;
    text-align: center;
    width: 100%;
    line-height: 1.3;
    word-break: break-word;
    hyphens: auto;
  }

  .header-logo {
    align-self: center;
    margin-right: 0;
    margin-top: 8px;
  }

  .audio-controls {
    justify-content: center;
  }

  .control-btn {
    padding: 10px 14px;
    font-size: 13px;
  }

  .item-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .item-icon {
    align-self: center;
  }

  .item-text {
    padding-left: 0;
  }
}

@media (max-width: 600px) {
  .modal-title {
    font-size: 16px;
    line-height: 1.2;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
}

@media (max-width: 480px) {
  .modal-container {
    width: 98%;
    margin: 10px;
    max-height: 85vh;
  }

  .modal-header {
    padding: 12px 16px;
  }

  .modal-title {
    font-size: 14px;
    line-height: 1.2;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .header-logo {
    height: 28px;
  }

  .modal-content {
    padding: 12px;
  }

  .audio-controls {
    gap: 8px;
  }

  .control-btn {
    padding: 8px 12px;
    font-size: 12px;
    flex: 1;
    max-width: 80px;
  }

  .section h3 {
    font-size: 16px;
  }

  .section li {
    font-size: 14px;
  }
}

@media (max-width: 360px) {
  .modal-title {
    font-size: 13px;
    line-height: 1.1;
  }

  .modal-header {
    padding: 10px 12px;
  }
}
</style>
